import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Preset theme colors
export const THEME_COLORS = [
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#10B981' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Slate', value: '#64748B' },
];

// Generate lighter/darker variants
function hexToHSL(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

function HSLToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

export function ThemeProvider({ children, user, team }) {
  const [themeColor, setThemeColor] = useState(null);

  useEffect(() => {
    // Determine which theme color to use
    let color = null;
    
    if (user?.role === 'fan') {
      // Fans use their personal theme color
      color = user.theme_color;
    } else if (team?.theme_color) {
      // Team members use team color
      color = team.theme_color;
    }
    
    setThemeColor(color);
    
    // Apply CSS variables
    if (color) {
      const hsl = hexToHSL(color);
      
      // Set CSS variables
      document.documentElement.style.setProperty('--theme-color', color);
      document.documentElement.style.setProperty('--theme-color-light', HSLToHex(hsl.h, hsl.s, Math.min(hsl.l + 35, 95)));
      document.documentElement.style.setProperty('--theme-color-dark', HSLToHex(hsl.h, hsl.s, Math.max(hsl.l - 15, 15)));
      document.documentElement.style.setProperty('--theme-color-hover', HSLToHex(hsl.h, hsl.s, Math.max(hsl.l - 10, 20)));
      document.documentElement.style.setProperty('--theme-color-muted', HSLToHex(hsl.h, Math.max(hsl.s - 30, 10), Math.min(hsl.l + 40, 90)));
    } else {
      // Reset to defaults
      document.documentElement.style.removeProperty('--theme-color');
      document.documentElement.style.removeProperty('--theme-color-light');
      document.documentElement.style.removeProperty('--theme-color-dark');
      document.documentElement.style.removeProperty('--theme-color-hover');
      document.documentElement.style.removeProperty('--theme-color-muted');
    }
  }, [user?.theme_color, user?.role, team?.theme_color]);

  return (
    <ThemeContext.Provider value={{ themeColor, setThemeColor, THEME_COLORS }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
